// move-rules.ts — Janus lane I2/I3 S3b (2026-09-28, owner: "this should all be frontend"): the move family's RULES in the browser — the
// twins of the decisions PHP move_inventory() (includes/functions/inventory/move_inventory_inc.php) makes for a pickup / drop / give /
// bag move, so the server only permits, CASes and persists what the browser decided:
//   find_stack_target()                         (:1236 — which existing stack a moved quantity merges into, if any)
//   validate_move_basics()                      (:735 — qty > 0; not a no-op)
//   validate_move_into_container_pounds_capacity (:833 — the IMMEDIATE container's pounds capacity)
//   validate_move_into_keyring_keys_only        (:893)
//   validate_move_does_not_create_inventory_cycle (:776)
//   native transfer AP (move / give: 1; shield move: 2; drop / impale: 0)
// Unreadable state answers `unknown`; the caller refuses before posting. Timed doffing uses the browser equip envelope.
export type Row = Record<string, unknown>;
export type Verdict = { ok: true } | { ok: false; error: string } | { unknown: string };

export const MOVE_RULE_CONSTANTS = {
  /** StatusEffect 'spoiling' — neither side of a merge may carry it. */
  spoiling: 'F7A1C3B2-5D64-4E18-9A70-2C8B41D5E093',
  /** CraftingWip::UNFINISHED_ITEM_SHELL — a crafting shell never stacks. */
  wipShell: '19D93387-AE21-4FB6-BD91-7420C320DB5F',
  shield: '376E7991-D1E3-D280-5186-F09AE6F90CD8',
  noStackTypes: ['vehicle_land', 'vehicle_water', 'vehicle_air', 'book'],
  containerLayers: ['carrying_capacity_cu_ft', 'carrying_capacity_cu_in', 'carrying_capacity_people', 'carrying_capacity_pounds', 'carrying_capacity_fixed_weight'],
} as const;

export interface MoveRuleReaders {
  /** layer_value on a (thin or fat) row. */
  value(row: Row | null | undefined, key: string): unknown;
  /** item_field('item_type'). */
  itemType(row: Row | null | undefined): string;
  /** inventory_field. */
  inventoryField(row: Row | null | undefined, key: string): unknown;
  /** The status item ids a row carries (target + caster sides). */
  statusItemIds(row: Row): string[];
}

const U = (v: unknown): string => String(v ?? '').trim().toUpperCase();
const UUID_RE = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i; // check_uuid()
const nonEmpty = (v: unknown): boolean => !(v === undefined || v === null || v === false || v === 0 || v === '' || v === '0' || (Array.isArray(v) && v.length === 0));
const num = (v: unknown): number => { const n = Number(Array.isArray(v) ? v[0] : v); return Number.isFinite(n) ? n : 0; };

/** is_inventory_arr_a_container() twin. */
export function isContainer(row: Row, r: MoveRuleReaders): boolean {
  return r.itemType(row) === 'container' || MOVE_RULE_CONSTANTS.containerLayers.some(k => nonEmpty(r.value(row, k)));
}

/**
 * find_stack_target() twin. `candidates` are the destination's live rows the page holds (the possessor's direct children; for a floor, its
 * floor rows). Returns the target row id, null (a new row / the whole row moves), or unknown when the page cannot decide the way PHP would.
 */
export function stackTarget(source: Row, candidates: Row[], dest: { isFloor: boolean; x: number; y: number; z: number }, effectiveWares: 0 | 1,
  r: MoveRuleReaders): { target: string | null } | { unknown: string } {
  const type = r.itemType(source);
  if (isContainer(source, r) || (MOVE_RULE_CONSTANTS.noStackTypes as readonly string[]).includes(type)) return { target: null };
  const itemId = U(r.inventoryField(source, 'inventory_item_id'));
  if (itemId === MOVE_RULE_CONSTANTS.wipShell) return { target: null };
  const name = String(r.inventoryField(source, 'inventory_name') ?? '');
  const sourceId = U(r.inventoryField(source, 'inventory_id'));
  const size = U(r.inventoryField(source, 'inventory_size'));
  const weight = r.inventoryField(source, 'weight_override');
  const template = String(r.inventoryField(source, 'enchantment_template_id') ?? '').trim();
  // ft_inventory_mesh_selection(): the stored selection is a UUID string or nothing — normalized exactly, so a difference is decisive.
  const meshSel = (row: Row): string => { const v = r.inventoryField(row, 'inventory_mesh_id') ?? r.inventoryField(row, 'mesh_id');
    const t = typeof v === 'string' ? v.trim() : ''; return UUID_RE.test(t) ? t.toUpperCase() : ''; };
  const mesh = meshSel(source);
  const transform = JSON.stringify(r.inventoryField(source, 'inventory_transform_3d') ?? r.inventoryField(source, 'transform_3d') ?? null);
  const spoils = (row: Row): boolean => r.statusItemIds(row).map(U).includes(MOVE_RULE_CONSTANTS.spoiling);
  const sourceSpoils = spoils(source);
  const survivors: Row[] = [];
  let unsure = false;
  for (const c of candidates) {
    // fetch_inventories() filters
    if (num(r.inventoryField(c, 'inventory_private')) !== 0 || num(r.inventoryField(c, 'inventory_deleted')) !== 0) continue;
    if (U(r.inventoryField(c, 'inventory_item_id')) !== itemId || String(r.inventoryField(c, 'inventory_name') ?? '') !== name) continue;
    if (nonEmpty(r.inventoryField(c, 'equipped'))) continue;
    if ((num(r.inventoryField(c, 'inventory_wares')) ? 1 : 0) !== effectiveWares) continue;
    if (dest.isFloor && (num(r.inventoryField(c, 'x_pos')) !== dest.x || num(r.inventoryField(c, 'y_pos')) !== dest.y || num(r.inventoryField(c, 'z_pos')) !== dest.z)) continue;
    // post-filters
    if (U(r.inventoryField(c, 'inventory_id')) === sourceId) continue;
    if (U(r.inventoryField(c, 'inventory_size')) !== size) continue;
    const cw = r.inventoryField(c, 'weight_override');
    if (((weight ?? null) === null) !== ((cw ?? null) === null) || (weight != null && Number(weight) !== Number(cw))) continue;
    if (String(r.inventoryField(c, 'enchantment_template_id') ?? '').trim() !== template) continue;
    if (sourceSpoils || spoils(c)) continue;
    // mesh selection is normalized exactly (above) — a different look never merges. transform_3d: PHP compares NORMALIZED values;
    // identical raw values are identical normalized — a raw difference the page cannot normalize is undecidable.
    if (meshSel(c) !== mesh) continue;
    const ct = JSON.stringify(r.inventoryField(c, 'inventory_transform_3d') ?? r.inventoryField(c, 'transform_3d') ?? null);
    if (ct !== transform) { unsure = true; continue; }
    survivors.push(c);
  }
  // PHP takes the FIRST survivor in SQL fetch order — the page cannot know that order when there are several; an undecidable candidate
  // could be that first one, so it blocks a decision even beside a definite survivor.
  if (unsure) return { unknown: 'stack-normalization' };
  if (survivors.length > 1) return { unknown: 'stack-ambiguous' };
  return { target: survivors.length ? U(r.inventoryField(survivors[0]!, 'inventory_id')) : null };
}

/** Worn gear with a doffing time starts through the browser equip envelope before it can move. */
export function needsDoff(moveType: string, row: Row, r: MoveRuleReaders): boolean {
  return moveType === 'move_inventory' && nonEmpty(r.inventoryField(row, 'equipped')) && nonEmpty(r.value(row, 'doffing_time_int'));
}

/** Transfer cost in combat; the move planner makes out-of-combat legs free. */
export function moveApCost(moveType: string, row: Row, r: MoveRuleReaders): number {
  if (moveType === 'drop_inventory' || moveType === 'impale_inventory') return 0;
  const shield = moveType === 'move_inventory' && r.itemType(row) === 'armor' && U(r.value(row, 'armor_type')) === MOVE_RULE_CONSTANTS.shield;
  return shield ? 2 : 1;
}

/**
 * Take All in combat (D-021, owner ruling 2026-09-30 — parity with the legacy partial haul): the rows land IN ORDER while the actor can pay
 * each one's AP leg; the first row it cannot pay stops the batch, keeping the rows already landed.
 * `count` = how many leading rows are taken; `refuse` when not even the first is affordable (the
 * legacy text for that row). Out of combat every row is taken; in combat with no AP value on the page → `unknown`.
 */
export function takeAllApPrefix(costs: number[], before: number | null, inCombat: boolean): { count: number; spent: number; refuse?: string; unknown?: true } {
  if (!inCombat) return { count: costs.length, spent: 0 };
  // Unreadable AP cannot decide a payable prefix; the caller refuses before posting.
  if (before === null || !Number.isFinite(before)) return { count: 0, spent: 0, unknown: true };
  let spent = 0;
  for (let i = 0; i < costs.length; i++) {
    const cost = costs[i]!;
    if (spent + cost > before) return i === 0 ? { count: 0, spent: 0, refuse: `In Combat: This action requires ${cost} AP!` } : { count: i, spent };
    spent += cost;
  }
  return { count: costs.length, spent };
}

/** validate_move_basics(). */
export function basicsVerdict(qty: number, fromId: string, toId: string, sameSlot: boolean, samePosition: boolean): Verdict {
  if (!(qty > 0)) return { ok: false, error: 'Inventory quantity must be greater than zero!' };
  if (U(fromId) === U(toId) && sameSlot && samePosition) return { ok: false, error: 'You must move the inventory to a different location or slot!' };
  return { ok: true };
}

/**
 * validate_move_into_container_pounds_capacity(): the IMMEDIATE container only; skipped when the row already sits in it. `heldLb` is the
 * container's computed.weight.held, `grossLb` the moved row's computed weight.gross for its whole stack (null = unreadable → unknown).
 */
export function capacityVerdict(dest: Row, source: Row, qty: number, heldLb: number | null, grossLb: number | null, r: MoveRuleReaders): Verdict {
  if (!isContainer(dest, r)) return { ok: true };
  const cap = r.value(dest, 'carrying_capacity_pounds');
  if (cap === null || cap === undefined || cap === '' || !Number.isFinite(Number(Array.isArray(cap) ? cap[0] : cap))) return { ok: true };
  if (U(r.inventoryField(source, 'possess_id')) === U(r.inventoryField(dest, 'inventory_id'))) return { ok: true };
  if (heldLb === null || grossLb === null) return { unknown: 'container-weight' };
  const stackQty = Math.max(1, num(r.inventoryField(source, 'item_qty')) || 1);
  const moved = (grossLb / stackQty) * qty;
  const remaining = num(cap) - heldLb;
  if (moved > remaining + 1e-9) {
    const fmt = (n: number): string => String(Math.round(n * 100) / 100);
    return { ok: false, error: `That container cannot hold that much weight (${fmt(moved)} lb exceeds ${fmt(Math.max(0, remaining))} lb remaining).` };
  }
  return { ok: true };
}

/** reject_non_key_into_keyring(). */
export function keyringVerdict(dest: Row, source: Row, r: MoveRuleReaders): Verdict {
  return r.itemType(dest) === 'keyring' && r.itemType(source) !== 'key' ? { ok: false, error: 'A keyring can only hold keys.' } : { ok: true };
}

/** validate_move_does_not_create_inventory_cycle() over the page's copy of the moved row's subtree. */
export function cycleVerdict(source: Row, destId: string, r: MoveRuleReaders): Verdict {
  const want = U(destId);
  if (U(r.inventoryField(source, 'inventory_id')) === want) return { ok: false, error: 'A container cannot be moved into itself or one of its contents.' };
  const walk = (list: unknown, depth: number): boolean => {
    if (depth > 64) return true;
    for (const n of Array.isArray(list) ? list : list && typeof list === 'object' ? Object.values(list as Row) : []) {
      const row = n && typeof n === 'object' && !Array.isArray(n) ? n as Row : null;
      if (!row) continue;
      if (U(r.inventoryField(row, 'inventory_id')) === want) return true;
      if (walk(row.inventory_in_container, depth + 1)) return true;
    }
    return false;
  };
  return walk(source.inventory_in_container, 0) ? { ok: false, error: 'A container cannot be moved into itself or one of its contents.' } : { ok: true };
}
